import React, { useCallback, useEffect, useState } from 'react';
import { Alert, alpha, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { listActiveSessions, revokeActiveSession, revokeOtherActiveSessions } from 'api/features';
import { GREEN, MONO } from './scanUi';

const panel = { borderRadius: 3, border: 1, borderColor: 'divider', bgcolor: 'background.paper' };
const Pill = ({ color, children }) => <Typography component="span" sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, px: 1.3, py: 0.5, borderRadius: 99, bgcolor: alpha(color, 0.16), color, whiteSpace: 'nowrap' }}>{children}</Typography>;
const when = (v) => { if (!v) return '-'; const d = new Date(v); return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); };
const fail = (e, fallback) => e?.response?.data?.detail || e?.message || fallback;

export default function ActiveSessionsPage() {
  const [rows, setRows] = useState(null);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState('');
  const [ask, setAsk] = useState(false);

  const load = useCallback(async () => {
    try { const d = await listActiveSessions(); setRows(Array.isArray(d?.results) ? d.results : []); } catch (e) { setError(fail(e, 'Could not load your sessions.')); setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, okText, failText) => {
    setWorking(true); setError(''); setDone('');
    try { const r = await fn(); setDone(r?.detail || okText); await load(); } catch (e) { setError(fail(e, failText)); } finally { setWorking(false); setAsk(false); }
  };

  if (rows === null) return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress sx={{ color: GREEN }} /></Box>;
  const others = rows.filter((r) => !r.current);
  return (
    <Stack spacing={2.5} className="fuzzscan-page" sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-end" flexWrap="wrap" gap={1.5}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Where you are signed in</Typography>
          <Typography color="text.secondary" variant="body2">{rows.length} session{rows.length === 1 ? '' : 's'}. Sign out any you do not recognise.</Typography>
        </Box>
        <Button variant="outlined" color="error" disabled={working || !others.length} onClick={() => setAsk(true)} sx={{ textTransform: 'none', fontWeight: 700 }}>
          Sign out everywhere else{others.length ? ` (${others.length})` : ''}
        </Button>
      </Stack>
      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
      {done && <Alert severity="success" onClose={() => setDone('')}>{done}</Alert>}

      <Box sx={{ ...panel, overflow: 'hidden' }}>
        {!rows.length && <Typography color="text.secondary" sx={{ p: 3 }}>No tracked sessions yet. New sign-ins appear here.</Typography>}
        {rows.map((r, i) => (
          <Stack key={r.id} direction="row" alignItems="center" gap={2} flexWrap="wrap" sx={{ px: 2.8, py: 1.8, borderTop: i ? 1 : 0, borderColor: 'divider' }}>
            <Box sx={{ flex: '2 1 260px', minWidth: 0 }}>
              <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                <Typography sx={{ fontWeight: 600 }}>{r.device_label || 'Unknown device'}</Typography>
                {r.current && <Pill color={GREEN}>THIS DEVICE</Pill>}
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: MONO }}>{r.ip_address || 'unknown address'}</Typography>
            </Box>
            <Box sx={{ flex: '1 1 160px' }}>
              <Typography variant="body2">Last used {when(r.last_seen_at)}</Typography>
              <Typography variant="caption" color="text.secondary">Ends {when(r.expires_at)}</Typography>
            </Box>
            <Button color="error" disabled={working || r.current} onClick={() => run(() => revokeActiveSession(r.id), 'Signed out.', 'Could not sign that session out.')} sx={{ textTransform: 'none', fontWeight: 700 }}>Sign out</Button>
          </Stack>
        ))}
      </Box>

      <Dialog open={ask} onClose={() => (working ? null : setAsk(false))} maxWidth="xs" fullWidth>
        <DialogTitle>Sign out everywhere else?</DialogTitle>
        <DialogContent><Typography variant="body2" color="text.secondary">{others.length} other session{others.length === 1 ? '' : 's'} will be signed out. This device stays signed in.</Typography></DialogContent>
        <DialogActions>
          <Button disabled={working} onClick={() => setAsk(false)}>Cancel</Button>
          <Button disabled={working} variant="contained" color="error" onClick={() => run(revokeOtherActiveSessions, 'Other sessions signed out.', 'Could not sign the other sessions out.')}>{working ? 'Working…' : 'Sign out'}</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
